import { ESdChangeReason, IAction, IActionParameter, ISymbol, ITargetScreenInputParam } from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { PropertySdHelper } from "../../../../shared/utility/property-sd-helper";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { SymbolPicker } from "./symbol-picker";

const OPEN_AS_OPTIONS: KuikaSelectboxOption[] = [
  { key: "currentPage", value: "currentPage", label: "Current Page" },
  { key: "newTab", value: "newTab", label: "New Tab" }
];

export interface IOpenPdfSymbolsGroupState {}

export interface IOpenPdfSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  targetScreenInputParams: ITargetScreenInputParam[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
  deleteTargetScreenInputParams: (parameterName: string) => void;
  changeActionParameter: (parameter: IActionParameter, symbol: ISymbol) => void;
}

class OpenPdfSymbolsGroup extends PureSdComponent<
  IOpenPdfSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IOpenPdfSymbolsGroupState
> {
  propertySdHelper: PropertySdHelper;

  constructor(props: IOpenPdfSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.propertySdHelper = new PropertySdHelper(this.getScreenDesignContextProviderValues);
  }

  getScreenDesignContextProviderValues = () => {
    return this.props.sd;
  };

  updateValues = (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => {
    if (!this.props.updateValues) return;
    this.props.updateValues(value, changeReason);
  };

  getParameterWithName = (name: "toUrl" | "openAs"): IActionParameter | undefined => {
    return this.props.parameters.find((x) => x.name === name);
  };

  getOpenAsSelectValue = () => {
    const openAs = this.getParameterWithName("openAs");

    if (!openAs) {
      return "";
    }

    return openAs.symbol.fixedValue;
  };

  renderPdfUrl = (parameter: IActionParameter): JSX.Element => {
    return (
      <>
        <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
          <ActionParameterTitle parameter={parameter} />
        </span>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          symbol={parameter.symbol}
          valueType={parameter.Typename}
          targetCardinality={parameter.cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) => {
            this.props.changeActionParameter(parameter, symbol);
          }}
          bindingParameter={parameter}
        />
      </>
    );
  };

  renderOpenAs = (parameter: IActionParameter): JSX.Element => {
    return (
      <>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Open As:
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            size="sm"
            style={{ width: "100%" }}
            value={this.getOpenAsSelectValue() ?? null}
            onChange={(value: string): void => this.onSelectOpenAs(value)}
            className="kuika_add_actions_inputs_size"
            options={OPEN_AS_OPTIONS}
          />
        </div>
      </>
    );
  };

  onSelectOpenAs = (value: string): void => {
    const params = _.cloneDeep(this.props.parameters);
    const openAsParameter = params.find((x) => x.name === "openAs");

    const valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;

    if (openAsParameter) {
      openAsParameter.symbol.fixedValue = value;
      this.updateValues(valuesToUpdate);
    }
  };

  render() {
    if (this.props.parameters.length <= 0) return <></>;

    return (
      <div style={{ width: "100%" }}>
        {this.props.parameters.map((parameter, id) => (
          <div key={id}>
            {parameter.name === "PdfUrl" && this.renderPdfUrl(parameter)}
            {parameter.name === "openAs" && this.renderOpenAs(parameter)}
          </div>
        ))}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    );
  };
}

const openPdfSymbolsGroup = withContext<IOpenPdfSymbolsGroupProps>(
  withSD<IOpenPdfSymbolsGroupProps>(OpenPdfSymbolsGroup)
);

export { openPdfSymbolsGroup as OpenPdfSymbolsGroup };
